import { cn } from '@/utils/cn'

export interface ProgressBarProps {
  value: number
  max?: number
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'gradient' | 'striped'
  showLabel?: boolean
  label?: string
  className?: string
}

const ProgressBar = ({
  value,
  max = 100,
  size = 'md',
  variant = 'default',
  showLabel = false,
  label,
  className,
}: ProgressBarProps) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

  const sizes = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  }

  const variants = {
    default: 'bg-light-icy-blue dark:bg-dark-muted-teal',
    gradient: 'bg-gradient-to-r from-light-icy-blue to-light-mauve dark:from-dark-muted-teal dark:to-dark-apricot-cream',
    striped: 'bg-light-icy-blue dark:bg-dark-muted-teal bg-[length:1rem_1rem] bg-[linear-gradient(45deg,rgba(255,255,255,.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,.15)_50%,rgba(255,255,255,.15)_75%,transparent_75%,transparent)]',
  }

  return (
    <div className={cn('w-full', className)}>
      {(showLabel || label) && (
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {label || 'Progress'}
          </span>
          {showLabel && (
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}
      <div
        className={cn(
          'w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden',
          sizes[size]
        )}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            variants[variant]
          )}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
        />
      </div>
    </div>
  )
}

// AI3C Coverage Progress
export interface AI3CCoverageProps {
  coverage: {
    activity: number
    item: number
    contradiction: number
    constraint: number
    criteria: number
  }
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const AI3CCoverageProgress = ({
  coverage,
  size = 'sm',
  className,
}: AI3CCoverageProps) => {
  const elements = [
    { key: 'activity', label: 'Activity', value: coverage.activity },
    { key: 'item', label: 'Item', value: coverage.item },
    { key: 'contradiction', label: 'Contradiction', value: coverage.contradiction },
    { key: 'constraint', label: 'Constraints', value: coverage.constraint },
    { key: 'criteria', label: 'Criteria', value: coverage.criteria },
  ]

  const colors = {
    activity: 'from-light-baby-pink to-light-baby-pink dark:from-dark-burnt-peach dark:to-dark-burnt-peach',
    item: 'from-light-icy-blue to-light-icy-blue dark:from-light-icy-blue dark:to-light-icy-blue',
    contradiction: 'from-light-mauve to-light-mauve dark:from-light-mauve dark:to-light-mauve',
    constraint: 'from-light-lemon-chiffon to-light-lemon-chiffon dark:from-dark-apricot-cream dark:to-dark-apricot-cream',
    criteria: 'from-light-frosted-mint to-light-frosted-mint dark:from-dark-muted-teal dark:to-dark-muted-teal',
  }

  const sizes = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-2.5',
  }

  return (
    <div className={cn('space-y-2', className)}>
      {elements.map(({ key, label, value }) => (
        <div key={key}>
          <div className="flex justify-between items-center mb-0.5">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {label}
            </span>
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
              {Math.round(value)}%
            </span>
          </div>
          <div
            className={cn(
              'w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden',
              sizes[size]
            )}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out bg-gradient-to-r',
                colors[key as keyof typeof colors]
              )}
              style={{ width: `${value}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default ProgressBar
